import { useState, useEffect } from 'react';
import api from '../api';

// Extra staff on particular days (the director, 28 Sep 2026): he knows who
// comes in extra only a few days ahead, and plans from the board, so it is
// entered there ("+ Staff") as well as on the Staffing page. Both show this one
// editor over the same rows, so they can never disagree.
//
// A row adds to the usual week: how many people (default 1; -1 for someone
// away), the hours each works (up to 8, a shift), and the shift they come in.
// Several rows on one day are fine; someone whose hours cover two shifts is
// two rows.

const CARD = '#1e2329', SUNK = '#131920', BORD = '#2a3040';
const MUTED = '#6b7280', DIM = '#9ca3af', TEXT = '#f0f2f5', TEAL = '#14b8a6', RED = '#fca5a5';
const SHIFTS = [[1, 'Morning'], [2, 'Afternoon'], [3, 'Night']];
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const weekday = (d) => (/^\d{4}-\d{2}-\d{2}$/.test(d || '') ? WEEKDAY[new Date(d + 'T12:00:00Z').getUTCDay()] : '');
const addDay = (d, n) => { const x = new Date(d + 'T12:00:00Z'); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
// The first working day after `d`: past weekends, public holidays and company
// closures (`closed`, from the server -- the same days the scheduler skips).
function nextWorkingDay(d, closed = {}) {
  let x = addDay(d, 1);
  for (let i = 0; i < 60; i++) {
    const dow = new Date(x + 'T12:00:00Z').getUTCDay();
    if (dow !== 0 && dow !== 6 && !closed[x]) return x;
    x = addDay(x, 1);
  }
  return addDay(d, 1);
}

const st = {
  input: { background: SUNK, border: `1px solid ${BORD}`, borderRadius: 8, color: TEXT, padding: '7px 9px', fontSize: 13, outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box' },
  btn:   { padding: '8px 16px', fontSize: 13, fontWeight: 600, border: `1px solid ${TEAL}`, borderRadius: 8, background: 'rgba(20,184,166,0.1)', color: TEAL, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' },
  ghost: { padding: '6px 11px', fontSize: 12, fontWeight: 600, border: `1px solid ${BORD}`, borderRadius: 7, background: 'transparent', color: DIM, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' },
  head:  { fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' },
};

const same = (r) => r.id && r.orig && r.orig.date === r.date && r.orig.shift_slot === r.shift_slot
  && Number(r.orig.people) === Number(r.people) && Number(r.orig.hours) === Number(r.hours);

const blankRow = (date, slot = 1) => ({ key: `new${Date.now()}${Math.random()}`, id: null, date, shift_slot: slot, people: 1, hours: 8 });

// The rows, their editing and saving. `onSaved` runs after a save went
// through; `onDirty` hears whether anything is unsaved. `startWithRow` opens
// with one row ready to fill in, as the board's pop-up does.
export function ExtraStaffEditor({ canEdit, onSaved, onDirty, padding = '14px 18px', startWithRow = false }) {
  const [rows, setRows] = useState(null);
  const [removed, setRemoved] = useState([]);
  const [today, setToday] = useState(null);
  const [closed, setClosed] = useState({});     // date -> holiday or closure, ahead
  const [starter, setStarter] = useState(null); // the ready row, as it was when offered
  const [state, setState] = useState(null);   // null | 'saving' | 'saved' | error sentence
  const [loadError, setLoadError] = useState(null);

  function load(offerRow = false) {
    api.get('/api/v1/staffing/changes')
      .then(r => {
        setToday(r.data.today);
        setClosed(r.data.closed || {});
        const list = (r.data.changes || []).map(c => ({
          key: `id${c.id}`, id: c.id, date: c.date, shift_slot: c.shift_slot,
          people: c.people, hours: Number(c.hours), note: c.note, orig: c,
        }));
        const ready = offerRow ? blankRow(nextWorkingDay(r.data.today, r.data.closed || {})) : null;
        setStarter(ready);
        setRows(ready ? [...list, ready] : list);
        setRemoved([]);
        setLoadError(null);
      })
      .catch(e => setLoadError(e.response?.data?.error || 'The extra staff could not be read just now.'));
  }
  useEffect(() => load(startWithRow && canEdit), []); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = (rows || []).filter(r => !same(r));
  const dirtyCount = pending.length + removed.length;
  // The ready row, untouched, is an offer rather than a change: closing
  // without using it discards nothing.
  const untouchedStarter = starter && (rows || []).some(r => r.key === starter.key
    && r.date === starter.date && r.shift_slot === starter.shift_slot && r.people === starter.people && r.hours === starter.hours);
  const unsaved = dirtyCount - (untouchedStarter ? 1 : 0);
  useEffect(() => { if (onDirty) onDirty(unsaved > 0); }, [unsaved, onDirty]);

  const edit = (key, k, v) => { setRows(rs => rs.map(r => (r.key === key ? { ...r, [k]: v } : r))); setState(null); };
  function remove(r) {
    setRows(rs => rs.filter(x => x.key !== r.key));
    if (r.id) setRemoved(ids => [...ids, r.id]);
    setState(null);
  }
  function addRow() {
    // The next working day after the row above, in the same shift; people and
    // hours start again at 1 and 8 (the director, 29 Sep). Weekends, holidays
    // and closures are stepped over.
    setRows(rs => {
      const last = rs[rs.length - 1];
      if (!last) return [...rs, blankRow(nextWorkingDay(today, closed))];
      const from = /^\d{4}-\d{2}-\d{2}$/.test(last.date || '') ? last.date : today;
      return [...rs, blankRow(nextWorkingDay(from, closed), last.shift_slot)];
    });
    setState(null);
  }
  function save() {
    setState('saving');
    api.put('/api/v1/staffing/changes', {
      rows: pending.map(r => ({ id: r.id || undefined, date: r.date, shift_slot: r.shift_slot, people: r.people, hours: r.hours })),
      remove: removed,
    })
      .then(() => { setState('saved'); load(false); if (onSaved) onSaved(); })
      .catch(e => setState(e.response?.data?.error || 'The extra staff could not be saved.'));
  }

  if (loadError) return <div style={{ padding, fontSize: 13, color: RED }}>{loadError}</div>;
  if (!rows) return <div style={{ padding, fontSize: 13, color: MUTED }}>Reading extra staff…</div>;

  return (
    <div>
      {rows.length === 0 ? (
        <div style={{ padding, fontSize: 12.5, color: MUTED, borderBottom: `1px solid ${BORD}` }}>
          No extra staff entered. Every day follows the usual week.
        </div>
      ) : (
        <div style={{ padding, borderBottom: `1px solid ${BORD}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ ...st.head, width: 196 }}>Date</span>
            <span style={{ ...st.head, width: 64 }}>People</span>
            <span style={{ ...st.head, width: 84 }}>Hours each</span>
            <span style={st.head}>Shift</span>
          </div>
          {rows.map(r => (
            <div key={r.key}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ width: 196, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="date" value={r.date || ''} min={today || undefined} disabled={!canEdit}
                    onChange={e => edit(r.key, 'date', e.target.value)} style={{ ...st.input, width: 150 }} />
                  <span style={{ fontSize: 12, color: DIM, width: 30 }}>{weekday(r.date)}</span>
                </div>
                <input type="number" step="1" value={r.people} disabled={!canEdit} title="How many people. -1 for someone away."
                  onChange={e => edit(r.key, 'people', e.target.value === '' ? '' : Number(e.target.value))}
                  style={{ ...st.input, width: 64, textAlign: 'center', color: Number(r.people) < 0 ? RED : TEXT }} />
                <input type="number" step="0.5" min="0.5" max="8" value={r.hours} disabled={!canEdit} title="Hours each of them works in that shift, up to 8."
                  onChange={e => edit(r.key, 'hours', e.target.value === '' ? '' : Number(e.target.value))}
                  style={{ ...st.input, width: 84, textAlign: 'center' }} />
                <div style={{ display: 'flex', gap: 4 }} role="radiogroup" aria-label="Shift">
                  {SHIFTS.map(([slot, name]) => {
                    const on = Number(r.shift_slot) === slot;
                    return (
                      <button key={slot} type="button" role="radio" aria-checked={on} disabled={!canEdit}
                        onClick={() => edit(r.key, 'shift_slot', slot)}
                        style={{ ...st.ghost, padding: '6px 10px', color: on ? TEAL : DIM, borderColor: on ? TEAL : BORD, background: on ? 'rgba(20,184,166,0.1)' : 'transparent', cursor: canEdit ? 'pointer' : 'default' }}>
                        {on && <i className="fa-solid fa-check" style={{ marginRight: 5, fontSize: 10 }} />}{name}
                      </button>
                    );
                  })}
                </div>
                {canEdit && (
                  <button type="button" onClick={() => remove(r)} title="Remove this row" aria-label="Remove this row"
                    style={{ ...st.ghost, padding: '6px 9px', marginLeft: 'auto' }}>
                    <i className="fa-solid fa-xmark" />
                  </button>
                )}
              </div>
              {(r.note || (!same(r) && r.id)) && (
                <div style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>
                  {r.note}{r.note && !same(r) && r.id ? ' · ' : ''}{!same(r) && r.id ? <span style={{ color: '#f59e0b' }}>changed</span> : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {canEdit && (
        <div style={{ padding, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button type="button" style={st.ghost} onClick={addRow}>+ add row</button>
          <span style={{ fontSize: 11.5, color: MUTED, flex: 1, minWidth: 180, lineHeight: 1.45 }}>
            -1 for someone away. Someone whose hours cover two shifts is two rows.
          </span>
          {state && state !== 'saving' && (
            <span style={{ fontSize: 12, color: state === 'saved' ? TEAL : RED, width: state === 'saved' ? 'auto' : '100%' }}>
              {state === 'saved' ? 'Saved' : state}
            </span>
          )}
          <button type="button" style={{ ...st.btn, opacity: dirtyCount ? 1 : 0.5, cursor: dirtyCount ? 'pointer' : 'default' }}
            onClick={save} disabled={!dirtyCount || state === 'saving'}>
            {state === 'saving' ? 'Saving…' : dirtyCount ? `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : 'Nothing to save'}
          </button>
        </div>
      )}
    </div>
  );
}

// The board's "+ Staff" pop-up: the same editor in a frame.
export default function ExtraStaffModal({ onClose, onSaved }) {
  const [dirty, setDirty] = useState(false);
  return (
    <div onClick={() => { if (!dirty) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, fontFamily: 'system-ui', padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', color: TEXT }}>
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${BORD}`, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Extra staff</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 3, lineHeight: 1.45 }}>
              People for moulding on particular days, added to the usual week. The board counts them straight away. The same list is on Admin → Staffing.
            </div>
          </div>
          <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', color: MUTED, fontSize: 16, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ overflowY: 'auto' }}>
          <ExtraStaffEditor canEdit startWithRow onSaved={onSaved} onDirty={setDirty} padding="14px 20px" />
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${BORD}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" style={st.ghost} onClick={onClose}>{dirty ? 'Discard changes and close' : 'Close'}</button>
        </div>
      </div>
    </div>
  );
}
